/**
 * Vista preliminar de resultados.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * POR QUÉ LA ADVERTENCIA VA ARRIBA Y NO EN UNA NOTA AL PIE
 * ──────────────────────────────────────────────────────────────────────────
 *
 * Estas cifras NO están ponderadas. §6 pondera por estrato, vivienda y
 * persona, y sin esos pesos los porcentajes describen a quienes contestaron,
 * no al padrón de Tecate. Con la muestra a medias, además, los conglomerados
 * levantados no están repartidos como el diseño prevé.
 *
 * El riesgo no es que alguien se equivoque leyendo: es que una captura de
 * pantalla de aquí circule como si fuera el resultado del estudio. Por eso la
 * advertencia es un bloque fijo arriba, con el avance a la vista, y cada
 * gráfica lleva su base impresa.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * UNA FORMA POR TIPO DE PREGUNTA
 * ──────────────────────────────────────────────────────────────────────────
 *
 *   categórica  barras ordenadas por frecuencia — la pregunta es «cuál pesa más»
 *   ordinal     barras en el ORDEN DE LA ESCALA — la pregunta es la forma del reparto
 *   batería     una fila por personaje, apilada por polaridad
 *   múltiple    barras, con aviso de que suman más de 100
 *   numérica    resumen y recorrido
 *   abierta     menciones ya codificadas; si no hay, cuántas esperan en la cola
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api, type QuestionResultView } from '../api/client';
import { BarChart, StatusChip, Tile, fmtDecimal, fmtInt } from '../components/charts';

export function ResultsPage() {
  const [bloque, setBloque] = useState<string>('');
  const [stratum, setStratum] = useState('');
  const [gender, setGender] = useState('');
  const [ageGroup, setAgeGroup] = useState('');

  const filtros = useMemo(() => {
    const f: Record<string, string> = {};
    if (stratum) f['stratum'] = stratum;
    if (gender) f['gender'] = gender;
    if (ageGroup) f['ageGroup'] = ageGroup;
    return f;
  }, [stratum, gender, ageGroup]);

  const datos = useQuery({
    queryKey: ['monitoring', 'results', filtros],
    queryFn: () => api.results(filtros),
    // Mantener lo anterior mientras llega lo nuevo evita que la pantalla
    // parpadee a «cargando» con cada cambio de filtro.
    placeholderData: (prev) => prev,
  });

  const bloques = useMemo(() => {
    const vistos = new Map<number, string>();
    for (const q of datos.data?.questions ?? []) vistos.set(q.blockId, q.blockTitle);
    return [...vistos.entries()].sort((a, b) => a[0] - b[0]);
  }, [datos.data]);

  const preguntas = (datos.data?.questions ?? []).filter(
    (q) => bloque === '' || String(q.blockId) === bloque,
  );

  if (datos.isLoading) {
    return (
      <div className="center-screen">
        <div className="spinner" />
      </div>
    );
  }
  if (datos.isError || !datos.data) {
    return (
      <div className="callout callout--danger">No se pudieron cargar los resultados.</div>
    );
  }

  const { base, target, instrumentVersion, filters, marginOfError } = datos.data;
  const avance = target && target > 0 ? (base / target) * 100 : 0;
  const hayFiltro = Boolean(stratum || gender || ageGroup);

  return (
    <>
      <div className="page-head">
        <h1 className="page-title">Resultados preliminares</h1>
        <p className="page-sub">
          Frecuencias sobre las entrevistas completadas · instrumento {instrumentVersion}
        </p>
      </div>

      {/**
       * El aviso es lo primero y no se puede cerrar. Una vista de resultados
       * a mitad de campo es exactamente el tipo de pantalla cuya captura
       * circula fuera de contexto.
       */}
      <div className="callout callout--warning">
        <span className="callout__title">
          <StatusChip status="warning">Sin ponderar · no son los resultados del estudio</StatusChip>
        </span>
        Estos porcentajes describen a <strong>quienes han contestado hasta ahora</strong>, no al
        padrón de Tecate. Falta la ponderación de §6 —estrato, vivienda y persona— y la muestra
        está incompleta, así que los conglomerados levantados todavía no se reparten como el
        diseño prevé. Para cifras citables, exporta la base y pondérala en R o SPSS.
      </div>

      <div className="grid grid--4">
        <Tile label="Entrevistas en la base" value={fmtInt(base)} reference="§2" />
        <Tile
          label="Meta de la oleada"
          value={target !== null ? fmtInt(target) : '—'}
          meta={`${fmtDecimal(avance, 1)}% del total`}
        />
        <Tile
          label="Margen del subgrupo"
          value={marginOfError !== null ? `±${fmtDecimal(marginOfError, 1)}` : '—'}
          unit="pp"
          reference="§2.1"
          meta="con efecto de diseño"
        />
        <Tile label="Bloques" value={fmtInt(bloques.length)} />
      </div>

      {base === 0 ? (
        <div className="callout callout--info">
          Todavía no hay entrevistas completadas en esta oleada. Las gráficas aparecerán conforme
          se sincronicen.
        </div>
      ) : null}

      <div className="filters">
        <label className="field" style={{ maxWidth: 220 }}>
          <span className="field__label">Estrato</span>
          <select className="input" value={stratum} onChange={(e) => setStratum(e.target.value)}>
            <option value="">Todos</option>
            {filters.strata.map((s) => (
              <option key={s.code} value={s.code}>
                {s.code} · {s.label}
              </option>
            ))}
          </select>
        </label>
        <label className="field" style={{ maxWidth: 180 }}>
          <span className="field__label">Sexo (S1)</span>
          <select className="input" value={gender} onChange={(e) => setGender(e.target.value)}>
            <option value="">Todos</option>
            {filters.genders.map((g) => (
              <option key={g.code} value={g.code}>
                {g.label}
              </option>
            ))}
          </select>
        </label>
        <label className="field" style={{ maxWidth: 240 }}>
          <span className="field__label">Grupo de edad (§3)</span>
          <select className="input" value={ageGroup} onChange={(e) => setAgeGroup(e.target.value)}>
            <option value="">Todos</option>
            {filters.ageGroups.map((g) => (
              <option key={g.code} value={g.code}>
                {g.label}
              </option>
            ))}
          </select>
        </label>
        <label className="field" style={{ maxWidth: 300 }}>
          <span className="field__label">Bloque</span>
          <select className="input" value={bloque} onChange={(e) => setBloque(e.target.value)}>
            <option value="">Todos los bloques</option>
            {bloques.map(([id, titulo]) => (
              <option key={id} value={String(id)}>
                Bloque {id} · {titulo}
              </option>
            ))}
          </select>
        </label>
        {hayFiltro ? (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            style={{ alignSelf: 'flex-end' }}
            onClick={() => {
              setStratum('');
              setGender('');
              setAgeGroup('');
            }}
          >
            Quitar filtros
          </button>
        ) : null}
      </div>

      {/**
       * El aviso de base pequeña aparece SÓLO al filtrar, y dice el margen en
       * vez de un «cuidado» genérico. Con 30 casos el margen ronda ±18
       * puntos: ver la cifra desalienta mejor que una advertencia.
       */}
      {hayFiltro && base > 0 && base < 100 ? (
        <div className="callout callout--warning">
          <span className="callout__title">
            <StatusChip status={base < 50 ? 'critical' : 'warning'}>
              Subgrupo de {fmtInt(base)} entrevistas
            </StatusChip>
          </span>
          Con esta base el margen es de{' '}
          <strong>±{marginOfError !== null ? fmtDecimal(marginOfError, 1) : '—'} puntos</strong>,
          así que casi cualquier diferencia que veas entre opciones cabe dentro del error. Sirve
          para detectar un problema de captura, no para leer una tendencia.
        </div>
      ) : null}

      {hayFiltro && base === 0 ? (
        <div className="callout callout--info">
          Ninguna entrevista cumple estos filtros. Si filtraste por sexo o edad, recuerda que sólo
          entran las que llegaron al Bloque 11 y contestaron S1 y S2.
        </div>
      ) : null}

      {preguntas.map((q) => (
        <QuestionCard key={q.questionCode} question={q} />
      ))}
    </>
  );
}

/* -------------------------------------------------------------------------- */

const FORM_LABEL: Record<string, string> = {
  categorical: 'Opción única',
  ordinal: 'Escala',
  battery: 'Batería',
  'battery-wide': 'Batería de muchas categorías',
  multi: 'Respuesta múltiple',
  numeric: 'Numérica',
  open: 'Abierta',
};

function QuestionCard({ question: q }: { question: QuestionResultView }) {
  const sinDatos = q.answered === 0;

  return (
    <div className="panel">
      <div className="panel__head">
        <div>
          <h2 className="panel__title">
            <span className="mono">{q.questionCode}</span> · {q.text}
          </h2>
          <p className="panel__note">
            Bloque {q.blockId} · {q.blockTitle} · {FORM_LABEL[q.form] ?? q.form}
            {!sinDatos ? (
              <>
                {' · '}
                {/**
                 * La base va junto a la gráfica, no en el encabezado de la
                 * página: cada pregunta tiene la suya y confundirlas es el
                 * error más fácil de cometer al leer frecuencias.
                 */}
                base {fmtInt(q.substantive)} sustantivas de {fmtInt(q.answered)} respuestas
              </>
            ) : null}
          </p>
        </div>
      </div>

      {sinDatos ? (
        <p className="faint">Sin respuestas todavía.</p>
      ) : (
        <QuestionChart question={q} />
      )}
    </div>
  );
}

/** Elige la gráfica según la forma que declaró el servidor. */
function QuestionChart({ question: q }: { question: QuestionResultView }) {
  if (q.form === 'numeric' && q.numeric) {
    return (
      <>
        <div className="grid grid--4">
          <Tile label="Respuestas" value={fmtInt(q.numeric.n)} />
          <Tile label="Mediana" value={fmtDecimal(q.numeric.median, 1)} />
          <Tile label="Media" value={fmtDecimal(q.numeric.mean, 1)} />
          <Tile
            label="Recorrido"
            value={`${fmtDecimal(q.numeric.min, 0)}–${fmtDecimal(q.numeric.max, 0)}`}
          />
        </div>
        {/**
         * La mediana va antes que la media a propósito: en edad e ingreso
         * declarados, unos pocos valores extremos mueven la media y no la
         * mediana, y la mediana es la que describe al entrevistado típico.
         */}
        <p className="faint">
          La mediana describe mejor al entrevistado típico cuando hay valores extremos.
        </p>
      </>
    );
  }

  if (q.form === 'open') {
    if (!q.mentions || q.mentions.length === 0) {
      return (
        <div className="callout callout--info" style={{ marginBottom: 0 }}>
          {fmtInt(q.answered)} respuestas de texto sin codificar. Las menciones aparecerán aquí
          conforme se clasifiquen en <strong>Codificación</strong> (§9.5); antes de eso, contarlas
          por literal mezclaría «agua» con «falta de agua».
        </div>
      );
    }
    const total = q.mentions.reduce((a, m) => a + m.count, 0);
    return (
      <BarChart
        rows={q.mentions.slice(0, 12).map((m) => ({
          key: m.label,
          label: m.label,
          value: (m.count / total) * 100,
          valueLabel: `${fmtDecimal((m.count / total) * 100, 1)}%`,
          tooltip: `${m.label}: ${fmtInt(m.count)} menciones de ${fmtInt(total)} codificadas`,
        }))}
        max={100}
      />
    );
  }

  if (q.form === 'battery' && q.rows) {
    return <BatteryChart question={q} />;
  }

  if (q.form === 'battery-wide' && q.rows) {
    return <WideBatteryChart question={q} />;
  }

  /* -------- Categórica, ordinal y múltiple: barras de porcentaje -------- */

  /**
   * La múltiple se divide entre ENTREVISTAS, no entre menciones: la pregunta
   * es «qué proporción de la gente mencionó esto», y por eso los porcentajes
   * suman más de 100. Dividir entre menciones respondería otra cosa.
   */
  const base = q.form === 'multi' ? q.answered : q.substantive;
  const sustantivas = q.options.filter((o) => !o.nonSubstantive);
  const noSustantivas = q.options.filter((o) => o.nonSubstantive);

  return (
    <>
      <BarChart
        rows={sustantivas.map((o) => ({
          key: o.code,
          label: o.label,
          value: base > 0 ? (o.count / base) * 100 : 0,
          valueLabel: `${fmtDecimal(base > 0 ? (o.count / base) * 100 : 0, 1)}%`,
          tooltip: `${o.label}: ${fmtInt(o.count)} de ${fmtInt(base)}`,
        }))}
        max={100}
      />

      {q.form === 'multi' ? (
        <p className="faint">
          Respuesta múltiple: los porcentajes son sobre entrevistas y suman más de 100.
        </p>
      ) : null}

      {/**
       * NS/NC va aparte y no dentro de las barras: está fuera del
       * denominador, y mezclarlo haría que los porcentajes de arriba
       * parecieran calculados sobre otra base.
       */}
      {noSustantivas.length > 0 ? (
        <p className="faint" style={{ marginTop: 10 }}>
          Fuera de la base:{' '}
          {noSustantivas.map((o) => `${o.label} ${fmtInt(o.count)}`).join(' · ')} — §9.4 vigila
          esta proporción.
        </p>
      ) : null}
    </>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * Batería: una fila por personaje, con su reparto apilado.
 *
 * Apilada y no una barra por opción porque lo que se compara entre personajes
 * es la FORMA del reparto, y con diez filas por cinco opciones en barras
 * sueltas habría cincuenta barras que nadie lee. El orden de los segmentos es
 * el de la escala, así que el ojo compara el mismo extremo en todas las filas.
 */
function BatteryChart({ question: q }: { question: QuestionResultView }) {
  if (!q.rows) return null;

  // Las opciones salen de la primera fila con datos: todas comparten escala.
  const escala = q.rows.find((r) => r.options.length > 0)?.options ?? [];

  return (
    <>
      <div className="row row--wrap" style={{ gap: 12, marginBottom: 12 }}>
        {escala.map((o, i) => (
          <span key={o.code} className="row" style={{ gap: 6, alignItems: 'center' }}>
            <span
              aria-hidden
              style={{
                width: 12,
                height: 12,
                borderRadius: 3,
                background: segmentColor(i, escala.filter((x) => !x.nonSubstantive).length, o.nonSubstantive),
                display: 'inline-block',
              }}
            />
            <span className="faint">{o.label}</span>
          </span>
        ))}
      </div>

      <div className="stack">
        {q.rows.map((fila) => {
          const total = fila.options.reduce((a, o) => a + o.count, 0);
          return (
            <div key={fila.code}>
              <div className="row row--between">
                <span className="strong">{fila.label}</span>
                <span className="faint">{fmtInt(total)}</span>
              </div>
              <div
                style={{
                  display: 'flex',
                  height: 18,
                  borderRadius: 4,
                  overflow: 'hidden',
                  background: 'var(--surface-2, #1a2233)',
                  marginTop: 4,
                }}
              >
                {fila.options.map((o, i) => {
                  const pct = total > 0 ? (o.count / total) * 100 : 0;
                  if (pct === 0) return null;
                  return (
                    <div
                      key={o.code}
                      title={`${fila.label} · ${o.label}: ${fmtInt(o.count)} (${fmtDecimal(pct, 1)}%)`}
                      style={{
                        width: `${pct}%`,
                        background: segmentColor(
                          i,
                          fila.options.filter((x) => !x.nonSubstantive).length,
                          o.nonSubstantive,
                        ),
                        // Separación de 2 px entre segmentos, como el resto de
                        // las gráficas del panel.
                        borderRight: '2px solid var(--surface)',
                      }}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

/**
 * Color de cada segmento de la escala.
 *
 * Usa la rampa secuencial validada del panel, de oscuro a claro, que es la
 * que ya se usa en el resto de las gráficas de datos. Las no sustantivas
 * salen en gris: no son un punto de la escala y pintarlas como si lo fueran
 * sugeriría un orden que no tienen.
 */
function segmentColor(index: number, substantiveCount: number, nonSubstantive: boolean): string {
  if (nonSubstantive) return 'var(--n-500, #6b7280)';
  const ramp = [
    'var(--seq-700)',
    'var(--seq-550)',
    'var(--seq-450)',
    'var(--seq-250)',
    'var(--seq-100)',
  ];
  /**
   * La rampa se reparte entre las opciones SUSTANTIVAS.
   *
   * Contando también las NS/NC —que van en gris y fuera de la escala— los
   * tonos de la escala real quedaban comprimidos en la mitad oscura de la
   * rampa, y «muy buena» y «buena» salían casi del mismo azul. El extremo
   * claro tiene que corresponder al extremo de la escala, no al último
   * segmento dibujado.
   */
  const pasos = Math.max(1, substantiveCount - 1);
  const paso = Math.min(ramp.length - 1, Math.round((index / pasos) * (ramp.length - 1)));
  return ramp[paso] ?? ramp[0]!;
}

/* -------------------------------------------------------------------------- */

/**
 * Batería con demasiadas categorías para apilar: se muestra la dominante.
 *
 * I4 pregunta con qué partido se relaciona a cada personaje, y tiene trece
 * opciones. Apiladas, los partidos menores caen por debajo del par de píxeles
 * y la fila deja de poder compararse con las demás, que es justo para lo que
 * sirve una batería. Y una rampa secuencial sobre categorías sin orden
 * insinuaría una gradación que no existe.
 *
 * Así que se responde la pregunta que de verdad se hace de esta batería: a
 * qué se asocia MÁS cada personaje y con cuánta fuerza. El resto queda en la
 * tabla, que es donde se consulta el detalle.
 */
function WideBatteryChart({ question: q }: { question: QuestionResultView }) {
  if (!q.rows) return null;

  const filas = q.rows.map((fila) => {
    const sustantivas = fila.options.filter((o) => !o.nonSubstantive);
    const base = sustantivas.reduce((a, o) => a + o.count, 0);
    const top = sustantivas[0];
    const segunda = sustantivas[1];
    return { fila, base, top, segunda };
  });

  return (
    <>
      <BarChart
        rows={filas
          .filter((f) => f.top)
          .map((f) => ({
            key: f.fila.code,
            label: `${f.fila.label} — ${f.top!.label}`,
            value: f.base > 0 ? (f.top!.count / f.base) * 100 : 0,
            valueLabel: `${fmtDecimal(f.base > 0 ? (f.top!.count / f.base) * 100 : 0, 1)}%`,
            tooltip:
              `${f.fila.label}: ${f.top!.label} ${fmtInt(f.top!.count)} de ${fmtInt(f.base)}` +
              (f.segunda ? ` · sigue ${f.segunda.label} con ${fmtInt(f.segunda.count)}` : ''),
          }))}
        max={100}
      />
      <p className="faint" style={{ marginTop: 10 }}>
        Se muestra la categoría dominante de cada fila sobre su base sustantiva. Apilar las{' '}
        {q.rows[0]?.options.filter((o) => !o.nonSubstantive).length ?? 0} opciones dejaría las
        menores por debajo de un par de píxeles. El detalle completo está en la exportación.
      </p>
    </>
  );
}
